import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  Vector3,
  type Scene,
} from 'three';
import {
  MUZZLE_SPEED,
  marchToImpact,
  sampleArc,
  type Impact,
} from '../utils/ballistics';
import { terrainNormal } from '../world/heightfield';
import type { HitTest } from './Shells';

/**
 * L'aide a la visee : le point d'impact predit, et la trajectoire complete
 * quand on est a la lunette.
 *
 * Elle n'approxime rien. `marchToImpact` est exactement la fonction qui sera
 * utilisee par l'obus : le cercle est donc la ou l'obus tombera, au
 * centimetre, y compris derriere une crete qu'on ne voit pas. Ce n'est pas de
 * la triche, c'est ce que fait un calculateur de tir -- et c'est ce qui rend
 * le tir courbe jouable. cf. doc/04-balistique.md
 */

/** Nombre de points de la trajectoire affichee. */
const ARC_POINTS = 56;

const FORWARD = new Vector3(0, 0, 1);

export interface Solution {
  /** Distance horizontale de la bouche au point d'impact, en metres. */
  range: number;
  /** Temps de vol jusqu'a l'impact, en secondes. */
  flightTime: number;
  /** Vrai si la trajectoire predite coupe une cible encore debout. */
  onTarget: boolean;
  /** Vrai si l'obus epuise son temps de vol sans rien toucher. */
  overshoot: boolean;
}

export class Sight {
  private readonly arc: Line;
  private readonly ring: Mesh;
  private readonly arcPositions = new Float32Array(ARC_POINTS * 3);
  private readonly impact: Impact = {
    point: new Vector3(),
    time: 0,
    hitGround: false,
  };

  private readonly velocity = new Vector3();
  private readonly normal = new Vector3();
  private readonly segA = new Vector3();
  private readonly segB = new Vector3();

  private readonly neutral = new Color(0xffe3a8);
  private readonly locked = new Color(0x6cff9a);

  private readonly solution: Solution = {
    range: 0,
    flightTime: 0,
    onTarget: false,
    overshoot: false,
  };

  constructor(scene: Scene) {
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(this.arcPositions, 3));
    this.arc = new Line(
      geo,
      new LineBasicMaterial({
        color: this.neutral,
        transparent: true,
        opacity: 0.5,
        // La trajectoire traverse les merlons : c'est tout l'interet. Sans
        // depthTest a false, la partie cachee disparait et on ne sait plus ou
        // l'obus retombe.
        depthTest: false,
      }),
    );
    this.arc.frustumCulled = false;
    this.arc.renderOrder = 6;
    this.arc.visible = false;
    scene.add(this.arc);

    this.ring = new Mesh(
      new RingGeometry(1.5, 2.0, 28),
      new MeshBasicMaterial({
        color: this.neutral,
        transparent: true,
        opacity: 0.85,
        depthTest: false,
      }),
    );
    this.ring.frustumCulled = false;
    this.ring.renderOrder = 6;
    this.ring.visible = false;
    scene.add(this.ring);
  }

  /**
   * Resout le tir courant et met a jour l'affichage.
   *
   * `hitTest` est celui des cibles : on verifie si l'arc predit coupe une
   * cible debout, et le cercle passe au vert. C'est la confirmation que le
   * joueur attend avant d'appuyer, surtout a 250 m ou la cible fait trois
   * pixels.
   */
  update(
    muzzle: Vector3,
    direction: Vector3,
    hitTest: HitTest,
    showArc: boolean,
  ): Solution {
    this.velocity.copy(direction).normalize().multiplyScalar(MUZZLE_SPEED);
    marchToImpact(muzzle, this.velocity, this.impact);

    const point = this.impact.point;
    this.solution.range = Math.hypot(point.x - muzzle.x, point.z - muzzle.z);
    this.solution.flightTime = this.impact.time;
    this.solution.overshoot = !this.impact.hitGround;

    sampleArc(muzzle, this.velocity, this.impact.time, ARC_POINTS, this.arcPositions);

    // La cible est-elle sur le chemin ? On reutilise les echantillons de
    // l'arc comme segments de test : le meme code que pour l'obus reel.
    this.solution.onTarget = false;
    for (let i = 0; i < ARC_POINTS - 1; i++) {
      this.segA.set(
        this.arcPositions[i * 3]!,
        this.arcPositions[i * 3 + 1]!,
        this.arcPositions[i * 3 + 2]!,
      );
      this.segB.set(
        this.arcPositions[(i + 1) * 3]!,
        this.arcPositions[(i + 1) * 3 + 1]!,
        this.arcPositions[(i + 1) * 3 + 2]!,
      );
      if (hitTest(this.segA, this.segB) !== null) {
        this.solution.onTarget = true;
        break;
      }
    }

    const color = this.solution.onTarget ? this.locked : this.neutral;

    const position = this.arc.geometry.getAttribute('position');
    position.needsUpdate = true;
    this.arc.visible = showArc;
    (this.arc.material as LineBasicMaterial).color.copy(color);

    // Le cercle se pose a plat sur le sol, oriente selon sa normale.
    this.ring.visible = this.impact.hitGround;
    if (this.impact.hitGround) {
      terrainNormal(point.x, point.z, this.normal);
      this.ring.position.copy(point).addScaledVector(this.normal, 0.12);
      this.ring.quaternion.setFromUnitVectors(FORWARD, this.normal);
      // Le cercle garde une taille apparente a peu pres constante : a 250 m,
      // un cercle de 2 m de rayon ne fait que quelques pixels.
      this.ring.scale.setScalar(1 + this.solution.range / 190);
      (this.ring.material as MeshBasicMaterial).color.copy(color);
    }

    return this.solution;
  }

  /**
   * Point d'impact predit, en coordonnees monde.
   *
   * C'est lui que vise la camera de lunette : pas le tube. Sur un vrai char,
   * la lunette du tireur reste sur l'objectif et le canon se releve au-dessus
   * d'elle de l'angle de hausse -- c'est tout l'interet d'un viseur
   * independant. Regarder dans l'axe du tube remplirait l'ecran de ciel des
   * qu'on tire a 250 m.
   */
  get impactPoint(): Vector3 {
    return this.impact.point;
  }

  hide(): void {
    this.arc.visible = false;
    this.ring.visible = false;
  }
}
